import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Undo2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { 
  getLastOperation, 
  clearLastOperation, 
  hasUndoableOperation,
  trackRedoOperation,
  clearLastUndoneOperation,
  type UndoOperation 
} from "@/lib/undoTracking";

interface UndoButtonProps {
  userId: string;
}

export const UndoButton = ({
 userId }: UndoButtonProps) => {
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [mobileBackdropActive, setMobileBackdropActive] = useState(false);
  const queryClient = useQueryClient();

  // Listen for mobile backdrop state changes
  useEffect(() => {
    const handleBackdropState = (e: CustomEvent) => {
      setMobileBackdropActive(e.detail.active);
    };
    
    window.addEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
    return () => window.removeEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
  }, []);

  // Check for undoable operations
  useEffect(() => {
    const checkUndo = () => {
      const hasUndo = hasUndoableOperation();
      setShowButton(hasUndo);
    };

    // Check immediately
    checkUndo();

    // Listen for tracking events
    const handleTracked = () => checkUndo();
    const handleCleared = () => setShowButton(false);

    window.addEventListener('undo-operation-tracked', handleTracked);
    window.addEventListener('undo-operation-cleared', handleCleared);

    // Check periodically (every 10 seconds) to hide button after timeout
    const interval = setInterval(checkUndo, 10000);

    return () => {
      window.removeEventListener('undo-operation-tracked', handleTracked);
      window.removeEventListener('undo-operation-cleared', handleCleared);
      clearInterval(interval);
    };
  }, []);

  const handleUndo = async () => {
    const operation = getLastOperation();
    if (!operation) {
      toast.error(t('notifications.toast.no_recent_operation_to_undo'));
      return;
    }

    // Check if operation belongs to current user
    if (operation.userId !== userId) {
      toast.error(t('notifications.toast.cannot_undo_another_user'));
      clearLastOperation();
      return;
    }

    setIsUndoing(true);

    try {
      if (operation.type === 'metric_value_update') {
        await undoMetricValueUpdate(operation);
        
        // Track the inverse operation for redo (swap previousValue and newValue)
        if (operation.metricUpdates) {
          const inverseUpdates = operation.metricUpdates.map(update => ({
            metricKey: update.metricKey,
            previousValue: update.previousValue, // What we just set it to
            newValue: update.newValue, // What it was before undo
          }));
          trackRedoOperation({
            type: 'metric_value_update',
            userId: operation.userId,
            entryDate: operation.entryDate,
            metricUpdates: inverseUpdates,
          });
        }
      } else if (operation.type === 'custom_metric_created') {
        await undoCustomMetricCreation(operation);
        // Note: Custom metric creation undo is not easily redoable, so we don't track it
        clearLastUndoneOperation();
      } else if (operation.type === 'builtin_metric_enabled') {
        await undoBuiltinMetricEnabled(operation);
        // Note: Built-in metric enablement undo is not easily redoable, so we don't track it
        clearLastUndoneOperation();
      }

      clearLastOperation();
      toast.success(t('notifications.toast.undone_successfully'));
    } catch (error) {
      console.error('[UndoButton] Undo failed:', error);
      toast.error(t('notifications.toast.failed_to_undo_operation'));
    } finally {
      setIsUndoing(false);
    }
  };

  const undoMetricValueUpdate = async (operation: UndoOperation) => {
    if (!operation.metricUpdates) return;

    // Build the update object for all metrics
    const updates: Record<string, number | null> = {};
    
    for (const update of operation.metricUpdates) {
      updates[update.metricKey] = update.previousValue;
    }

    // Update the daily entry
    const { error } = await supabase
      .from("daily_entries")
      .update(updates)
      .eq("user_id", operation.userId)
      .eq("entry_date", operation.entryDate);

    if (error) throw error;

    // Invalidate relevant queries
    queryClient.invalidateQueries({ queryKey: ["daily-entry", operation.userId, operation.entryDate] });
    queryClient.invalidateQueries({ queryKey: ["daily-entries", operation.userId] });
  };

  const undoCustomMetricCreation = async (operation: UndoOperation) => {
    if (!operation.customMetricCreated) return;

    const { metricKey, metricId } = operation.customMetricCreated;

    // Delete the custom metric
    const { error: metricError } = await supabase
      .from("custom_metrics")
      .delete()
      .eq("user_id", operation.userId)
      .eq("metric_key", metricKey);

    if (metricError) throw metricError;

    // Also clear any values that were set for this metric
    const columnName = `${metricKey}_value`;
    const { error: valueError } = await supabase
      .from("daily_entries")
      .update({ [columnName]: null })
      .eq("user_id", operation.userId)
      .eq("entry_date", operation.entryDate);

    // Don't throw on value error - the entry might not exist
    if (valueError) {
      console.warn('[UndoButton] Failed to clear metric value:', valueError);
    }

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ["custom-metrics", operation.userId] });
    queryClient.invalidateQueries({ queryKey: ["daily-entry", operation.userId, operation.entryDate] });
    queryClient.invalidateQueries({ queryKey: ["daily-entries", operation.userId] });
  };

  const undoBuiltinMetricEnabled = async (operation: UndoOperation) => {
    if (!operation.builtinMetricEnabled) return;

    const { metricKey, category } = operation.builtinMetricEnabled;

    // Fetch current preferences
    const { data: currentPrefs, error: fetchError } = await supabase
      .from("metric_preferences")
      .select("*")
      .eq("user_id", operation.userId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    if (!currentPrefs) {
      console.warn('[UndoButton] No metric preferences found');
      return;
    }

    // Remove the metric from the category
    const currentMetrics = (currentPrefs[category] || []) as string[];
    const updatedMetrics = currentMetrics.filter(key => key !== metricKey);

    // Update preferences
    const { error: updateError } = await supabase
      .from("metric_preferences")
      .update({ [category]: updatedMetrics })
      .eq("user_id", operation.userId);

    if (updateError) throw updateError;

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ["metric-preferences", operation.userId] });
  };

  // Hide button if mobile backdrop is active or if there's no operation to undo
  if (!showButton || mobileBackdropActive) return null;

  return (
    <Button
      onClick={handleUndo}
      disabled={isUndoing}
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground opacity-50 animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <Undo2 className="h-4 w-4" />
    </Button>
  );
};
